import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('raftaar_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('raftaar_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials),
  me: () => api.get('/auth/me'),
};

// Vehicles
export const vehiclesApi = {
  getAll: () => api.get('/vehicles'),
  getById: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
  uploadImage: (id, formData) => api.post(`/vehicles/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadDocument: (id, docType, formData) => api.post(`/vehicles/${id}/upload-document?doc_type=${docType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (vehicleId, imageId) => api.delete(`/vehicles/${vehicleId}/images/${imageId}`),
  deleteDocument: (vehicleId, docId) => api.delete(`/vehicles/${vehicleId}/documents/${docId}`),
};

// Drivers
export const driversApi = {
  getAll: () => api.get('/drivers'),
  getById: (id) => api.get(`/drivers/${id}`),
  create: (data) => api.post('/drivers', data),
  update: (id, data) => api.put(`/drivers/${id}`, data),
  delete: (id) => api.delete(`/drivers/${id}`),
  uploadImage: (id, formData) => api.post(`/drivers/${id}/upload-image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadDocument: (id, docType, formData) => api.post(`/drivers/${id}/upload-document?doc_type=${docType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteImage: (driverId, imageId) => api.delete(`/drivers/${driverId}/images/${imageId}`),
  deleteDocument: (driverId, docId) => api.delete(`/drivers/${driverId}/documents/${docId}`),
  getProfitability: () => api.get('/drivers/profitability'),
  getDriverProfitability: (id) => api.get(`/drivers/${id}/profitability`),
};

// Daily Driver Records
export const dailyDriverRecordsApi = {
  getAll: (params) => api.get('/driver-daily-records', { params }),
  getById: (id) => api.get(`/driver-daily-records/${id}`),
  create: (data) => api.post('/driver-daily-records', data),
  update: (id, data) => api.put(`/driver-daily-records/${id}`, data),
  delete: (id) => api.delete(`/driver-daily-records/${id}`),
  getMonthlySummary: (year, month) => api.get('/driver-daily-records/summary/monthly', { params: { year, month } }),
  getYearlySummary: (year) => api.get('/driver-daily-records/summary/yearly', { params: { year } }),
};

// Expenses
export const expensesApi = {
  getAll: (type) => api.get('/expenses', { params: { expense_type: type } }),
  getById: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data) => api.put(`/expenses/${id}`, data),
  delete: (id) => api.delete(`/expenses/${id}`),
  getCategories: () => api.get('/expense-categories'),
};

// Revenue
export const revenueApi = {
  getAll: () => api.get('/revenue'),
  create: (data) => api.post('/revenue', data),
  update: (id, data) => api.put(`/revenue/${id}`, data),
};

// Invoices
export const invoicesApi = {
  getAll: () => api.get('/invoices'),
  getById: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  updatePayment: (id, status, mode) => api.put(`/invoices/${id}/payment?payment_status=${status}${mode ? `&payment_mode=${mode}` : ''}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getCompanyInfo: () => api.get('/company-info'),
};

// Maintenance
export const maintenanceApi = {
  getAll: () => api.get('/maintenance'),
  create: (data) => api.post('/maintenance', data),
  update: (id, data) => api.put(`/maintenance/${id}`, data),
};

// Compliance
export const complianceApi = {
  getAll: () => api.get('/compliance'),
  create: (data) => api.post('/compliance', data),
  update: (id, data) => api.put(`/compliance/${id}`, data),
};

// Customers
export const customersApi = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
};

// Staff
export const staffApi = {
  getAll: () => api.get('/staff'),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
};

// Dashboard
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
  getAlerts: () => api.get('/dashboard/alerts'),
  getExpenseBreakdown: () => api.get('/dashboard/expense-breakdown'),
  getVehicleProfitability: (id) => api.get(`/dashboard/vehicle/${id}/profitability`),
};

export default api;
